<!-- CHAIN REACTION — every answer has to link to the one before it.

     A chain is a sequence of links, and the chain BREAKS on a wrong one, which
     costs one of three lives and starts a fresh chain. Get to the end of a
     chain and a new one starts too, so a good run is several chains deep
     rather than one long one. The strip along the top shows what he has
     built so far, which is the whole satisfaction of the mode.

     Options are shuffled per link — the bank stores the right answer first for
     readability, and rendering them in that order would make the game trivial. -->
<template>
  <IonPage>
    <AppHeader title="Chain Reaction" back="/tabs/play" />

    <IonContent :fullscreen="true">
      <template v-if="!started">
        <div class="pad" style="padding-top: var(--sp-6)">
          <p class="ex-display lg">Chain Reaction</p>
          <p class="ex-sub">
            Start with a word. Every answer has to link to the last one — a
            country, then a player from there, then his club. Keep it going as
            long as you can. A wrong link drops the chain and costs a life.
          </p>
        </div>
        <ModeRecord mode="chain-reaction" />

        <div class="pad" style="margin-top: var(--sp-5)">
          <IonButton expand="block" size="large" @click="begin">Start a chain</IonButton>
        </div>
      </template>

      <template v-else-if="chain && link">
        <GameHud :score="score" :streak="streak" label="Chain" :value="chainLength" :lives="lives" />
        <div v-if="timed" class="clock-bar" :class="clockClass" aria-hidden="true"><i :style="{ width: clockPct + '%' }" /></div>

        <div class="chain-strip">
          <span class="chain-seed">{{ chain.seed }}</span>
          <template v-for="(built, i) in builtLinks" :key="i">
            <span class="arrow" aria-hidden="true">&rarr;</span>
            <span class="chain-link">{{ built }}</span>
          </template>
        </div>

        <div class="q-stage" ref="stageRef" tabindex="-1" style="padding-top: var(--sp-2)">
          <span class="q-topic">{{ chain.template }}</span>
          <p class="q-prompt">{{ link.prompt }}</p>

          <div class="answers">
            <button
              v-for="opt in shuffledOptions"
              :key="opt"
              type="button"
              class="answer"
              :class="optClass(opt)"
              :disabled="revealed"
              @click="choose(opt)"
            >
              {{ opt }}
              <span v-if="revealed && optClass(opt) === 'right'" class="sr-only"> — correct answer</span>
              <span v-else-if="revealed && optClass(opt) === 'wrong'" class="sr-only"> — your answer, incorrect</span>
            </button>
          </div>
        </div>
      </template>

      <div class="tail" />
    </IonContent>

    <VerdictFlash :text="verdict.text" :why="verdict.why" :good="verdict.good" />
    <AusBonus :when="ausNow" @done="ausNow = false" />
  </IonPage>
</template>

<script setup>
import { ref, computed, onUnmounted, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { IonPage, IonContent, IonButton } from '@ionic/vue';
import { AppHeader, announce } from '../core/ui';
import GameHud from '../components/GameHud.vue';
import ModeRecord from '../components/ModeRecord.vue';
import VerdictFlash from '../components/VerdictFlash.vue';
import AusBonus from '../components/AusBonus.vue';
import { drawQuestions, pointsFor, isAusAnswer, shuffle } from '../game/engine.js';
import { drawOptions } from '../game/profile.js';
import { useCountdown } from '../game/clock.js';
import { paced, timed } from '../game/pace.js';
import { regainLife } from '../game/lives.js';
import { finishRun } from '../game/run.js';
import { useStopOnLeave, useResetOnEnter } from '../game/guard.js';
import { pun } from '../game/puns.js';
import { haptic } from '../core/util.js';

const router = useRouter();
const PER_LINK_MS = 12000;

const started = ref(false);
const chains = ref([]);
const chainIdx = ref(0);
const linkIdx = ref(0);
const builtLinks = ref([]);
const revealed = ref(false);
const chosen = ref('');
const shuffledOptions = ref([]);
const ausNow = ref(false);
const stageRef = ref(null);

const score = ref(0);
const streak = ref(0);
const chainLength = ref(0);
const lives = ref(3);
const bestChain = ref(0);
const correct = ref(0);
const wrong = ref(0);
const ausCount = ref(0);
const verdict = ref({ text: '', why: '', good: true });

const clock = useCountdown(paced(PER_LINK_MS), () => breakChain(pun('clock')));
const chain = computed(() => chains.value[chainIdx.value] ?? null);
const link = computed(() => chain.value?.links[linkIdx.value] ?? null);
const answerText = computed(() => link.value?.options[link.value.answer] ?? '');
const clockPct = computed(() => (clock.remaining.value / paced(PER_LINK_MS)) * 100);
const clockClass = computed(() => {
  const s = clock.remaining.value / 1000;
  return s <= 3 ? 'panic' : s <= 6 ? 'warn' : '';
});

let advanceTimer = null;
let ended = false;

function dealOptions() {
  shuffledOptions.value = shuffle(link.value?.options ?? []);
}

function begin() {
  chains.value = drawQuestions('chain-reaction', { ...drawOptions.value, count: 30 });
  started.value = true;
  chainIdx.value = 0;
  linkIdx.value = 0;
  builtLinks.value = [];
  dealOptions();
  clock.reset(paced(PER_LINK_MS));
  if (timed.value) clock.start();
  // The Start screen's button is gone from the DOM the instant this runs, so
  // without this a keyboard or screen-reader user is dropped back to <body>
  // with no idea the game has begun.
  nextTick(() => stageRef.value?.focus());
}

function optClass(opt) {
  if (!revealed.value) return '';
  if (opt === answerText.value) return 'right';
  if (opt === chosen.value) return 'wrong';
  return 'muted';
}

function choose(opt) {
  if (revealed.value || ended || !link.value) return;
  clock.stop();
  revealed.value = true;
  chosen.value = opt;

  if (opt !== answerText.value) {
    breakChain();
    return;
  }

  const aus = isAusAnswer(chain.value, opt);
  if (aus) { ausCount.value += 1; ausNow.value = true; }
  score.value += pointsFor({
    difficulty: chain.value.difficulty,
    streak: streak.value,
    aus,
    // A link deep in a chain is worth more than the first one — that is what
    // makes a long chain worth protecting rather than just longer.
    multiplier: 1 + chainLength.value * 0.15,
  });
  streak.value += 1;
    regainLife(lives);
  chainLength.value += 1;
  bestChain.value = Math.max(bestChain.value, chainLength.value);
  correct.value += 1;
  builtLinks.value = [...builtLinks.value, opt];
  haptic('success');
  verdict.value = { text: streak.value >= 3 ? pun('streak') : pun('correct'), why: link.value.reason, good: true };

  clearTimeout(advanceTimer);
  advanceTimer = setTimeout(nextLink, 1300);
}

function nextLink() {
  if (ended) return;
  verdict.value = { text: '', why: '', good: true };
  revealed.value = false;
  chosen.value = '';
  linkIdx.value += 1;

  // Chain complete: start a fresh one and keep the length running, so
  // finishing a chain is rewarded rather than reset.
  if (linkIdx.value >= (chain.value?.links.length ?? 0)) {
    chainIdx.value += 1;
    linkIdx.value = 0;
    builtLinks.value = [];
    if (chainIdx.value >= chains.value.length) {
      chains.value = [...chains.value, ...drawQuestions('chain-reaction', { ...drawOptions.value, count: 20 })];
    }
  }

  dealOptions();
  clock.reset(paced(PER_LINK_MS));
  if (timed.value) clock.start();
  // The prompt text changes silently under a screen reader otherwise — the
  // container already has focus from the run's start, but its content just
  // swapped without a peep.
  announce(`Next link: ${link.value?.prompt ?? ''}`);
}

function breakChain(text) {
  if (ended) return;
  revealed.value = true;
  wrong.value += 1;
  streak.value = 0;
  lives.value -= 1;
  haptic('error');
  verdict.value = {
    text: text ?? pun('wrong'),
    why: link.value?.reason ?? '',
    good: false,
  };
  clearTimeout(advanceTimer);
  // A broken chain cannot be resumed — the chain IS the mode, and picking up
  // mid-chain after getting a link wrong would be answering a question he has
  // already seen the answer to. Spending a life starts a fresh chain instead.
  advanceTimer = setTimeout(() => (lives.value <= 0 ? end() : freshChain()), 2100);
}

// Same as finishing a chain, except the running length resets: he did not
// earn this one.
function freshChain() {
  if (ended) return;
  verdict.value = { text: '', why: '', good: true };
  revealed.value = false;
  chosen.value = '';
  chainLength.value = 0;
  builtLinks.value = [];
  linkIdx.value = 0;
  chainIdx.value += 1;
  if (chainIdx.value >= chains.value.length) {
    chains.value = [...chains.value, ...drawQuestions('chain-reaction', { ...drawOptions.value, count: 20 })];
  }
  dealOptions();
  clock.reset(paced(PER_LINK_MS));
  if (timed.value) clock.start();
  announce(`Fresh chain. ${chain.value?.seed ?? ''}. Next link: ${link.value?.prompt ?? ''}`);
}

async function end() {
  if (ended) return;
  ended = true;
  clock.stop();
  clearTimeout(advanceTimer);
  await finishRun({
    mode: 'chain-reaction',
    score: score.value,
    lastedMs: 0,
    correct: correct.value,
    wrong: wrong.value,
    bestChain: bestChain.value,
    ausBonuses: ausCount.value,
  });
  router.replace('/tabs/play/results');
}

// Everything this screen has running, stopped. Idempotent: it is called on
// every leave, on abandon, and again before every re-entry.
function teardown() {
  ended = true;
  clock.stop();
  clearTimeout(advanceTimer);
}

// Back to the pre-game screen. Without this, "Go again" returned to this same
// cached instance still holding the finished run.
function resetRun() {
  teardown();
  ended = false;
  // Every ref back to the literal it was declared with — the state a
  // fresh mount would have. Resetting only the run flags left the LAST
  // question's verdict and its revealed answer painted over the new
  // board, so a new game opened showing why the previous one ended.
  started.value = false;
  chains.value = [];
  chainIdx.value = 0;
  linkIdx.value = 0;
  builtLinks.value = [];
  revealed.value = false;
  chosen.value = '';
  shuffledOptions.value = [];
  ausNow.value = false;
  stageRef.value = null;
  score.value = 0;
  streak.value = 0;
  chainLength.value = 0;
  lives.value = 3;
  bestChain.value = 0;
  correct.value = 0;
  wrong.value = 0;
  ausCount.value = 0;
  verdict.value = { text: '', why: '', good: true };
}

useStopOnLeave(teardown);
useResetOnEnter(resetRun);

onUnmounted(() => { ended = true; clearTimeout(advanceTimer); });
</script>
